import { chatDrawerActions } from '../../../ducks/chatDrawer'
import { SessionToolCall, SessionTranscription } from '../types'
import { convertToolCallToButtonAction } from './toolCallUtils'

interface VoiceMessageItem {
  type: 'transcription' | 'toolCall'
  content?: string
  direction?: 'send' | 'receive'
  toolName?: string
  arguments?: any
  timestamp: number
  sharedWithContexts: string[]
  failed?: boolean
  error?: string
}

/**
 * Import voice session transcriptions and tool calls into the chat drawer
 */
export const importVoiceToChat = (
  transcriptions: SessionTranscription[],
  toolCalls: SessionToolCall[],
  setSessionTranscriptions: (transcriptions: SessionTranscription[]) => void,
  setSessionToolCalls: (toolCalls: SessionToolCall[]) => void
) => {
  try {
    if (!window.reduxStore || (transcriptions.length === 0 && toolCalls.length === 0)) {
      return
    }

    // Combine transcriptions and tool calls, sort by timestamp
    const allItems: VoiceMessageItem[] = [
      ...transcriptions.map((t) => ({
        type: 'transcription' as const,
        content: t.content,
        direction: t.direction,
        timestamp: t.timestamp,
        sharedWithContexts: t.sharedWithContexts,
      })),
      ...toolCalls.map((tc) => ({
        type: 'toolCall' as const,
        toolName: tc.toolName,
        arguments: tc.arguments,
        timestamp: tc.timestamp,
        sharedWithContexts: [],
        failed: tc.failed,
        error: tc.error,
      })),
    ].sort((a, b) => a.timestamp - b.timestamp)

    // Convert to messages
    const voiceMessages = allItems.map((item) => {
      if (item.type === 'transcription') {
        return {
          content: item.content || '',
          direction: item.direction || 'receive',
          isRead: true,
          origin: 'voice' as const,
          sharedWithContexts: item.sharedWithContexts,
          buttonActions: [],
        }
      } else {
        // Tool call - convert to message with buttonAction (only for successful calls)
        const buttonAction = item.failed
          ? null // Don't show button action for failed tool calls
          : convertToolCallToButtonAction({
              toolName: item.toolName!,
              arguments: item.arguments,
              timestamp: item.timestamp,
            })

        // For failed tool calls, show error message in content
        const content = item.failed ? `Tool call ${item.toolName} failed: ${item.error || 'Unknown error'}` : '' // Empty content for successful tool calls - ActionConfirmation will show the collapsed state

        return {
          content,
          direction: 'receive' as const, // Tool calls are from Ada
          isRead: true,
          origin: 'voice' as const,
          sharedWithContexts: [],
          buttonActions: buttonAction ? [buttonAction] : [],
        }
      }
    })

    window.reduxStore.dispatch(chatDrawerActions.importVoiceMessages(voiceMessages))

    // Clear both transcriptions and tool calls
    setSessionTranscriptions([])
    setSessionToolCalls([])
  } catch (error) {
    console.error('Error importing voice to chat:', error)
  }
}
